<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { endOfDay, startOfDay, startOfMonth } from 'date-fns';
import ReportsAPI from 'dashboard/api/reports';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import Button from 'dashboard/components-next/button/Button.vue';
import ReportHeader from './components/ReportHeader.vue';
import ReportMetricCard from './components/ReportMetricCard.vue';
import AiUsageBalanceCard from './components/aiUsage/AiUsageBalanceCard.vue';
import AiUsageFeatureList from './components/aiUsage/AiUsageFeatureList.vue';
import AiUsageLogsTable from './components/aiUsage/AiUsageLogsTable.vue';
import {
  formatCurrency,
  formatTokens,
} from './components/aiUsage/aiUsagePresentation';

const { t } = useI18n();
const { isAdmin } = useAdmin();
const accountId = useMapGetter('getCurrentAccountId');
let requestId = 0;

const PLACEHOLDER = '—';

const dateRange = ref([startOfMonth(new Date()), new Date()]);
const rangeType = ref(DATE_RANGE_TYPES.MONTH_TO_DATE);
const isLoading = ref(false);
const hasLoaded = ref(false);
const page = ref(1);
const perPage = 50;
const summary = ref({});
const byFeature = ref([]);
const events = ref([]);
const meta = ref({});
const openAiBilling = ref({});

const totalPages = computed(() => meta.value.total_pages || 0);
const totalEvents = computed(() => meta.value.total_count || 0);
const unpricedCalls = computed(() => Number(summary.value.unpriced_calls || 0));
const isFirstLoad = computed(() => isLoading.value && !hasLoaded.value);

const metricValue = value => (hasLoaded.value ? value : PLACEHOLDER);

const formatPeriod = () => {
  const [start, end] = dateRange.value;
  if (!start || !end) return PLACEHOLDER;

  return `${new Date(start).toLocaleDateString('pt-BR')} - ${new Date(end).toLocaleDateString('pt-BR')}`;
};

const toEpoch = date => Math.floor(new Date(date).getTime() / 1000);

const loadReport = async () => {
  if (!isAdmin.value || !accountId.value) return;
  requestId += 1;
  const currentRequest = requestId;
  const requestedAccountId = accountId.value;
  isLoading.value = true;
  try {
    const [start, end] = dateRange.value;
    const { data } = await ReportsAPI.getAiUsage({
      from: toEpoch(startOfDay(new Date(start))),
      to: toEpoch(endOfDay(new Date(end))),
      page: page.value,
      perPage,
    });
    if (
      currentRequest !== requestId ||
      !isAdmin.value ||
      requestedAccountId !== accountId.value
    )
      return;
    summary.value = data.summary || {};
    openAiBilling.value = data.openai_billing || {};
    byFeature.value = data.by_feature || [];
    events.value = data.events || [];
    meta.value = data.meta || {};
    hasLoaded.value = true;
  } catch {
    if (currentRequest === requestId && isAdmin.value)
      useAlert(t('AI_USAGE_REPORT.LOAD_ERROR'));
  } finally {
    if (currentRequest === requestId) isLoading.value = false;
  }
};

const onDateRangeChanged = ([start, end, type]) => {
  dateRange.value = [start, end];
  rangeType.value = type;
  page.value = 1;
  loadReport();
};

const applyFilters = () => {
  page.value = 1;
  loadReport();
};

const clearFilters = () => {
  dateRange.value = [startOfMonth(new Date()), new Date()];
  rangeType.value = DATE_RANGE_TYPES.MONTH_TO_DATE;
  page.value = 1;
  loadReport();
};

watch(page, loadReport);
watch(
  [accountId, isAdmin],
  () => {
    requestId += 1;
    summary.value = {};
    openAiBilling.value = {};
    byFeature.value = [];
    events.value = [];
    meta.value = {};
    hasLoaded.value = false;
    isLoading.value = false;
    const isFirstPage = page.value === 1;
    page.value = 1;
    if (isFirstPage) loadReport();
  },
  { immediate: true }
);
onBeforeUnmount(() => {
  requestId += 1;
});
</script>

<template>
  <div v-if="isAdmin && accountId" class="flex w-full flex-col">
    <ReportHeader
      :header-title="t('AI_USAGE_REPORT.TITLE')"
      :header-description="t('AI_USAGE_REPORT.DESCRIPTION')"
      icon="i-hugeicons-ai-brain-01"
    />

    <div class="flex flex-col gap-5">
      <section
        class="flex flex-wrap items-center gap-3 rounded-xl border border-n-weak bg-n-solid-1 px-4 py-3 dark:border-n-strong"
        :aria-label="t('AI_USAGE_REPORT.FILTERS.PERIOD')"
      >
        <span
          class="inline-flex items-center gap-1.5 text-xs font-medium text-n-slate-11"
        >
          <span class="i-hugeicons-calendar-03 size-4" aria-hidden="true" />
          {{ t('AI_USAGE_REPORT.FILTERS.PERIOD') }}
        </span>
        <WootDatePicker
          v-model:date-range="dateRange"
          v-model:range-type="rangeType"
          @date-range-changed="onDateRangeChanged"
        />
        <div class="ml-auto flex flex-wrap items-center gap-2">
          <Button
            :label="t('AI_USAGE_REPORT.FILTERS.CLEAR')"
            icon="i-hugeicons-refresh"
            color="slate"
            variant="ghost"
            size="sm"
            :disabled="isLoading"
            @click="clearFilters"
          />
          <Button
            :label="t('AI_USAGE_REPORT.FILTERS.APPLY')"
            icon="i-hugeicons-filter-horizontal"
            size="sm"
            :is-loading="isLoading"
            @click="applyFilters"
          />
        </div>
      </section>

      <div
        class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4"
        :aria-busy="isLoading ? 'true' : undefined"
      >
        <ReportMetricCard
          :label="t('AI_USAGE_REPORT.METRICS.TOKENS')"
          :info-text="t('AI_USAGE_REPORT.METRICS.TOKENS_INFO')"
          :value="metricValue(formatTokens(summary.total_tokens))"
          icon="i-hugeicons-coins-01"
        />

        <div
          data-test-id="aiUsageCostCard"
          class="flex min-w-0 flex-col gap-2 rounded-xl border border-n-weak bg-n-solid-1 px-4 py-3.5 dark:border-n-strong"
        >
          <h3
            class="m-0 flex min-w-0 items-center gap-1.5 text-xs font-medium text-n-slate-11"
          >
            <span
              class="i-hugeicons-money-01 size-4 shrink-0 text-n-slate-10"
              aria-hidden="true"
            />
            <span class="truncate">
              {{ t('AI_USAGE_REPORT.METRICS.COST') }}
            </span>
            <span
              v-tooltip="t('AI_USAGE_REPORT.METRICS.COST_INFO')"
              class="i-hugeicons-information-circle size-3.5 shrink-0 text-n-slate-10"
              :aria-label="t('AI_USAGE_REPORT.METRICS.COST_INFO')"
              role="img"
            />
          </h3>
          <p class="mb-0 text-2xl font-semibold tabular-nums text-n-slate-12">
            {{ metricValue(formatCurrency(summary.cost_brl, 'BRL')) }}
          </p>
          <template v-if="hasLoaded">
            <p class="mb-0 text-xs tabular-nums text-n-slate-11">
              {{
                t('AI_USAGE_REPORT.METRICS.COST_USD', {
                  usd: formatCurrency(summary.cost_usd, 'USD'),
                  rate: formatCurrency(summary.usd_brl_rate, 'BRL'),
                })
              }}
            </p>
            <p
              v-if="unpricedCalls"
              class="mb-0 text-xs tabular-nums text-n-amber-11"
            >
              {{
                t(
                  'AI_USAGE_REPORT.METRICS.COST_UNPRICED',
                  { count: formatTokens(unpricedCalls) },
                  unpricedCalls
                )
              }}
            </p>
          </template>
        </div>

        <ReportMetricCard
          :label="t('AI_USAGE_REPORT.METRICS.CALLS')"
          :info-text="t('AI_USAGE_REPORT.METRICS.CALLS_INFO')"
          :value="metricValue(formatTokens(summary.calls_count))"
          icon="i-hugeicons-flash"
        />
        <ReportMetricCard
          :label="t('AI_USAGE_REPORT.METRICS.PERIOD')"
          :info-text="t('AI_USAGE_REPORT.METRICS.PERIOD_INFO')"
          :value="formatPeriod()"
          icon="i-hugeicons-calendar-03"
        />
      </div>

      <div class="grid gap-5 xl:grid-cols-3">
        <AiUsageFeatureList
          class="xl:col-span-2"
          :items="byFeature"
          :loading="isFirstLoad"
        />
        <AiUsageBalanceCard
          class="self-start"
          :billing="openAiBilling"
          :loading="isFirstLoad"
        />
      </div>

      <AiUsageLogsTable
        v-model:page="page"
        :events="events"
        :loading="isLoading"
        :total-pages="totalPages"
        :total-count="totalEvents"
      />
    </div>
  </div>
</template>
